// =============================================================================
// ui/tabs/Results.tsx — the backtest Results board. #58.
// -----------------------------------------------------------------------------
// ── ⚠ THIS IS A FOLD-IN, NOT A RELOCATION ──
//
// Every other tab in this port moved a renderer out of `app/index.html`. This one
// came from `app/backtest-dashboard.html` — a SEPARATE 917-line document that the
// old client loads in an iframe and feeds one `postMessage`. Folding it into a
// tab deletes the iframe, the message, the ready handshake and the height
// reporting: about 55 lines of plumbing that existed only because the two halves
// were separate documents.
//
// The board was read by SERVING IT LOCALLY with a known payload and reading the
// rendered DOM, rather than by reading its source. That is what settled the tile
// labels, the table's nine columns and its default sort — beta's file still
// carries markup for things the board no longer shows.
//
// ── ⚠ NO DATE RANGE, NO ACCOUNT SCOPE, NO GLOBAL FILTER ──
//
// Backtests are not trades. They are not scoped to an account and the toolbar's
// range does not apply to them. The board has its own two filters — instrument
// and pattern — and beta is the same. This is not an omission.
//
// ── ⚠ THE DISPLAY-UNIT SWITCH DELIBERATELY SKIPS THIS TAB ──
//
// Beta's own note on `PNL_UNITS`: *"the backtest Results dashboard is out of
// scope (already points/R)"*. There is no dollar view here to convert, so the
// unit picker has nothing to do. Do not wire it in without a reason.
//
// ── 🔴 WHAT IS NOT PORTED IN THIS PASS, AND WHY IT IS NAMED ON SCREEN ──
//
// Beta's two toggles, both OFF by default there, are absent here:
//
//   Live comparison — needs every metric-eligible TRADE, the widest read in this
//                     client. Beta gets it free because it holds the whole book
//                     in memory; here it is a deliberate request that this tab
//                     does not yet make.
//   Rule-set A/B    — needs the pattern-version lifecycle gate, which this client
//                     has no equivalent for. Porting the card without it would
//                     make it always-on, which is a behaviour change, not a port.
//
// A tab that silently lacks two controls is indistinguishable from a broken one,
// so the board says so where they would be.
// =============================================================================

import { useMemo, useState } from 'react';
import {
  EMPTY_FILTER, applyBoardFilter, boardStats, confluenceGroups, cumulativeSeries,
  curveBuckets, dailyNet, instrumentOptions, maxDrawdown, patternGroups,
  patternOptions, sessionGroups, stackingLadder, toBoardSamples, underwaterSeries,
  type TimeSlice,
} from '../../selectors/backtestBoard';
import { monthPrefix, weekStart } from '../../selectors/calendar';
import { useBacktests } from '../state/backtests';
import { usePatterns } from '../state/patterns';
import { usePreferences } from '../state/preferences';
import { confluenceMarkReader } from '../../selectors/confluenceEdge';
import { ResultsKpis } from './ResultsKpis';
import { ResultsCurve } from './ResultsCurve';
import { ResultsPlaybook } from './ResultsPlaybook';
import { ResultsTiming } from './ResultsTiming';
import { ResultsConfluence } from './ResultsConfluence';
import { ResultsDaily } from './ResultsDaily';

/**
 * ⚠ Below this a session is shown but never NAMED as best or worst. Three
 * samples can top a win-rate list on luck, and the callout is the one line on
 * that card that makes a claim.
 */
const TIMING_MIN_SAMPLE = 5;

export interface ResultsTabProps {
  /** Send the reader to another tab — the empty state points at Backtesting. */
  go?: ((tab: string) => void) | undefined;
}

export function ResultsTab({ go }: ResultsTabProps) {
  const { phase, records, error } = useBacktests();
  const { patterns } = usePatterns();
  const { prefs } = usePreferences();

  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [slice, setSlice] = useState<TimeSlice>('weekly');
  const [monthIndex, setMonthIndex] = useState(0);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  // ⚠ Every sample, before the board's own filters — the filter OPTIONS are
  // built from this. Building them from the filtered set would make choosing one
  // instrument remove every other option.
  /**
   * ⚠ ONE READER PER REGISTRY, built here because this is the layer that can
   * see preferences. It is the same function the Confluence Edge card and the
   * Coach's Report read through — which is the whole point: this board used to
   * interpret the confluence column itself and disagreed with both.
   */
  const confMarks = useMemo(
    () => confluenceMarkReader(prefs?.confluences), [prefs?.confluences],
  );

  const all = useMemo(
    () => toBoardSamples(records ?? [], patterns ?? [], confMarks),
    [records, patterns, confMarks],
  );
  const samples = useMemo(() => applyBoardFilter(all, filter), [all, filter]);

  const stats = useMemo(() => boardStats(samples), [samples]);
  const series = useMemo(() => cumulativeSeries(samples), [samples]);
  const underwater = useMemo(() => underwaterSeries(series), [series]);
  const buckets = useMemo(
    () => (slice === 'weekly'
      ? curveBuckets(samples, weekStart, (b) => `wk ${b.slice(5)}`)
      : curveBuckets(samples, monthPrefix, (b) => b)),
    [samples, slice],
  );
  const daily = useMemo(() => dailyNet(samples), [samples]);
  const months = useMemo(
    () => [...new Set([...daily.keys()].map(monthPrefix))].sort(),
    [daily],
  );
  const daySamples = useMemo(
    () => (selectedDay ? samples.filter((s) => s.date === selectedDay) : []),
    [samples, selectedDay],
  );

  if (phase === 'loading') return <p className="muted">Loading your backtests…</p>;

  // ⚠ The error state, not a board of em-dashes. Zeros on a performance board
  // read as "your setups made nothing", which is a different claim from "we
  // could not read them".
  if (phase === 'failed') {
    return (
      <div className="card" role="alert">
        <h3>Couldn’t load your backtests</h3>
        <p className="muted">Nothing has been changed. {error}</p>
      </div>
    );
  }

  // ⚠ Beta's empty state is a card with a way out, not a blank board.
  if (all.length === 0) {
    return (
      <div className="card bd-empty" data-testid="results-empty">
        <h3>No completed backtests yet</h3>
        <p className="muted">
          This board reads the samples you record on the Backtesting tab. Log a few
          with an entry and an exit and the performance picture builds itself.
        </p>
        {go ? (
          <button type="button" className="btn primary" onClick={() => go('backtests')}>
            Go to Backtesting
          </button>
        ) : null}
      </div>
    );
  }

  const monthClamped = Math.min(monthIndex, Math.max(0, months.length - 1));

  return (
    <div className="bd-board" data-testid="results-board">
      {/* ── controls ─────────────────────────────────────────────────────── */}
      <div className="bd-bar">
        <label className="bd-ctl">
          <span className="bd-ctl-label">Instrument</span>
          <select
            value={filter.instrument}
            onChange={(e) => setFilter((f) => ({ ...f, instrument: e.target.value }))}
          >
            <option value="all">All instruments</option>
            {instrumentOptions(all).map((o) => (
              <option key={o.value} value={o.value}>{o.label} ({o.n})</option>
            ))}
          </select>
        </label>

        <label className="bd-ctl">
          <span className="bd-ctl-label">Pattern</span>
          <select
            value={filter.pattern}
            onChange={(e) => setFilter((f) => ({ ...f, pattern: e.target.value }))}
          >
            <option value="all">All patterns</option>
            {patternOptions(all).map((o) => (
              <option key={o.value} value={o.value}>{o.label} ({o.n})</option>
            ))}
          </select>
        </label>

        <div className="bd-ctl">
          <span className="bd-ctl-label">Time slice</span>
          <div className="bd-seg">
            {(['weekly', 'monthly'] as const).map((k) => (
              <button
                key={k} type="button" className={slice === k ? 'on' : ''}
                onClick={() => setSlice(k)}
              >
                {k === 'weekly' ? 'Weekly' : 'Monthly'}
              </button>
            ))}
          </div>
        </div>

        {/* 🔴 NAMED, NOT HIDDEN. See the header — both need data this tab does
            not yet request, and a silently missing control is indistinguishable
            from a broken one. */}
        <div className="bd-absent">
          Rule-set A/B and Live comparison aren’t here yet — they need the pattern
          version history and your live trades, which this board doesn’t load.
        </div>
      </div>

      {filter.instrument !== 'all' || filter.pattern !== 'all' ? (
        <div className="bd-banner">
          Showing{' '}
          {filter.instrument === 'all' ? 'all instruments' : filter.instrument}
          {' · '}
          {filter.pattern === 'all'
            ? 'all patterns'
            : patternOptions(all).find((p) => p.value === filter.pattern)?.label ?? 'one pattern'}
          <button type="button" className="bd-clear" onClick={() => setFilter(EMPTY_FILTER)}>
            Clear
          </button>
        </div>
      ) : null}

      <ResultsKpis stats={stats} />

      <ResultsCurve
        series={series} underwater={underwater}
        maxDrawdown={maxDrawdown(series)} samples={samples.length} buckets={buckets}
        bucketNoun={slice === "weekly" ? "week" : "month"}
      />

      <ResultsPlaybook
        groups={patternGroups(samples)}
        focused={filter.pattern === 'all' ? null : filter.pattern}
        onFocus={(id) => setFilter((f) => ({ ...f, pattern: id ?? 'all' }))}
        sparkline={(g) => cumulativeSeries(g.samples)}
      />

      <div className="bd-pair">
        <ResultsTiming groups={sessionGroups(samples)} minSample={TIMING_MIN_SAMPLE} />
        <ResultsConfluence
          byConfluence={confluenceGroups(samples)}
          ladder={stackingLadder(samples)}
        />
      </div>

      <ResultsDaily
        months={months}
        monthIndex={monthClamped}
        onMonth={(i) => { setMonthIndex(i); setSelectedDay(null); }}
        daily={daily}
        selectedDay={selectedDay}
        onSelectDay={setSelectedDay}
        daySamples={daySamples}
      />
    </div>
  );
}
